/* eslint-disable no-undefined */
import React, { useState, useCallback, useEffect, useRef, createElement, cloneElement, Children, isValidElement } from 'react';
import PropTypes from 'prop-types';
import warning from 'warning';
import TabTemplate from './TabTemplate';
import * as colors from '@constants/colors';
import InkBar from './InkBar';
import './tabs.scss';

const Tabs = ({
  children,
  contentContainerClassName,
  contentContainerStyle,
  initialSelectedIndex = 0,
  inkBarStyle,
  onChange = () => {},
  onScroll,
  style,
  tabItemContainerStyle,
  tabItemWidth,
  tabTemplate,
  tabTemplateStyle,
  value,
  ...other
}) => {
  // Unique per Tabs instance, so tab/tabpanel id pairs can't collide if more than one
  // Tabs component is ever mounted on the same page at once.
  const instanceIdRef = useRef(`tabs-${Math.random()
    .toString(36)
    .slice(2, 9)}`);

  const tabListRef = useRef(null);

  const getTabs = useCallback(() => {
    const tabs = [];
    Children.forEach(children, (tab) => {
      if (isValidElement(tab)) {
        tabs.push(tab);
      }
    });
    return tabs;
  }, [children]);

  const getTabCount = useCallback(() => getTabs().length, [getTabs]);

  const getValueLink = useCallback(() => ({
    value,
    requestChange: onChange
  }), [value, onChange]);

  const getSelectedIndex = useCallback(() => {
    const valueLink = getValueLink();
    let selectedIdx = -1;
    getTabs().forEach((tab, index) => {
      if (valueLink.value === tab.props.value) {
        selectedIdx = index;
      }
    });
    return selectedIdx;
  }, [getValueLink, getTabs]);

  // Initialize selected index
  const computeInitialIndex = () => {
    const valueLink = getValueLink();
    if (typeof valueLink.value !== 'undefined') {
      return getSelectedIndex();
    }
    return initialSelectedIndex < getTabCount() ? initialSelectedIndex : 0;
  };

  const [selectedIndex, setSelectedIndex] = useState(computeInitialIndex);

  // Update selected index when value prop changes
  useEffect(() => {
    const valueLink = getValueLink();
    if (typeof valueLink.value !== 'undefined') {
      setSelectedIndex(getSelectedIndex());
    }
  }, [value, getValueLink, getSelectedIndex]);

  const handleTabTouchTap = useCallback((tabValue, event, tab) => {
    const valueLink = getValueLink();
    const index = tab.props.index;

    if ((valueLink.value && valueLink.value !== tabValue) ||
      selectedIndex !== index) {
      valueLink.requestChange(tabValue, event, tab);
    }

    setSelectedIndex(index);

    if (tab.props.onActive) {
      tab.props.onActive(tab);
    }
  }, [getValueLink, selectedIndex]);

  const getSelected = useCallback((tab, index) => {
    const valueLink = getValueLink();
    return valueLink.value ? valueLink.value === tab.props.value :
      selectedIndex === index;
  }, [getValueLink, selectedIndex]);

  // Roving tabindex (WAI-ARIA Tabs pattern): only the selected tab is in the Tab order -
  // Left/Right/Home/End move focus (and activate, matching this component's existing
  // click-to-activate behavior) between tabs instead.
  const handleTabListKeyDown = useCallback(event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const tabEls = Array.from(tabListRef.current.querySelectorAll('[role="tab"]'));
    const currentIndex = tabEls.indexOf(document.activeElement);
    if (currentIndex === -1) {
      return;
    }
    event.preventDefault();
    let nextIndex;
    if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabEls.length) % tabEls.length;
    } else if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabEls.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else {
      nextIndex = tabEls.length - 1;
    }
    tabEls[nextIndex].focus();
    tabEls[nextIndex].click();
  }, []);

  const valueLink = getValueLink();
  const tabValue = valueLink.value;
  const tabContent = [];
  const tabCount = getTabCount();
  const defaultWidth = tabCount > 0 ? 100 / tabCount : 100;
  const width = tabItemWidth || `${defaultWidth}%`;
  const widthNum = tabItemWidth ? parseInt(tabItemWidth.replace('px', ''), 10) : defaultWidth;

  const tabs = getTabs().map((tab, index) => {
    warning(tab.type && tab.type.muiName === 'Tab',
      `Material-UI: Tabs only accepts Tab Components as children.
      Found ${tab.type.muiName || tab.type} as child number ${index + 1} of Tabs`);

    warning(!tabValue || typeof tab.props.value !== 'undefined',
      `Material-UI: Tabs value prop has been passed, but Tab ${index}
      does not have a value prop. Needs value if Tabs is going
      to be a controlled component.`);

    const isSelected = getSelected(tab, index);
    const tabKey = typeof tab.props.value === 'string' ? tab.props.value : index;
    const tabId = `${instanceIdRef.current}-tab-${tabKey}`;
    const panelId = `${instanceIdRef.current}-tabpanel-${tabKey}`;

    tabContent.push(tab.props.children ?
      createElement(tabTemplate || TabTemplate, {
        key: index,
        'aria-labelledby': tabId,
        id: panelId,
        selected: isSelected,
        style: tabTemplateStyle
      }, tab.props.children) : undefined);

    return cloneElement(tab, {
      key: index,
      index,
      // Only set aria-controls when the tabpanel above was actually created - a tab whose
      // content is lazily rendered (tab.props.children falsy until selected) would otherwise
      // point at an id that doesn't exist in the DOM yet.
      'aria-controls': tab.props.children ? panelId : undefined,
      'aria-selected': isSelected,
      id: tabId,
      role: 'tab',
      selected: isSelected,
      tabIndex: isSelected ? 0 : -1,
      width,
      onClick: handleTabTouchTap
    });
  });

  const realSelectedIndex = valueLink.value ? getSelectedIndex() : selectedIndex;

  const inkBar = realSelectedIndex !== -1 ? (
    <InkBar
      color={colors.secondaryColor}
      left={`${widthNum * realSelectedIndex}${tabItemWidth ? 'px' : '%'}`}
      width={width}
      style={inkBarStyle}
    />
  ) : null;

  const inkBarContainerWidth = tabItemContainerStyle ?
    tabItemContainerStyle.width : '100%';

  return (
    <div
      style={style}
      {...other}
      styleName="tabs-container"
    >
      {/* The tablist itself is never in the Tab order under the roving-tabindex pattern -
          only the selected tab is (see tabIndex above) - so it correctly has no tabIndex
          of its own despite handling keydown. */}
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
      <div
        ref={tabListRef}
        role="tablist"
        style={tabItemContainerStyle}
        styleName="tabs-item-container"
        onKeyDown={handleTabListKeyDown}
      >
        {tabs}
      </div>
      <div
        style={{ width: inkBarContainerWidth }}
        styleName="tabs-ink-bar-container"
      >
        {inkBar}
      </div>
      <div
        className={contentContainerClassName}
        onScroll={onScroll}
        styleName="tabs-content-container"
        style={contentContainerStyle}
      >
        {tabContent}
      </div>
    </div>
  );
};

Tabs.propTypes = {
  /**
   * Should be used to pass `Tab` components.
   */
  children: PropTypes.node,
  /**
   * The css class name of the root element.
   */
  className: PropTypes.string,
  /**
   * The css class name of the content's container.
   */
  contentContainerClassName: PropTypes.string,
  /**
   * Override the inline-styles of the content's container.
   */
  contentContainerStyle: PropTypes.object,
  /**
   * Specify initial visible tab index.
   * If `initialSelectedIndex` is set but larger than the total amount of specified tabs,
   * `initialSelectedIndex` will revert back to default.
   * If `initialSelectedIndex` is set to any negative value, no tab will be selected intially.
   */
  initialSelectedIndex: PropTypes.number,
  /**
   * Override the inline-styles of the InkBar.
   */
  inkBarStyle: PropTypes.object,
  /**
   * Called when the selected value change.
   */
  onChange: PropTypes.func,
  /**
   * Callback for content onScroll event.
   */
  onScroll: PropTypes.func,
  /**
   * Override the inline-styles of the root element.
   */
  style: PropTypes.object,
  /**
   * Override the inline-styles of the tab-labels container.
   */
  tabItemContainerStyle: PropTypes.object,
  /**
   * Override the default tab label item and ink bar width.
   */
  tabItemWidth: PropTypes.string,
  /**
   * Override the default tab template used to wrap the content of each tab element.
   */
  tabTemplate: PropTypes.func,
  /**
   * Override the inline-styles of the tab template.
   */
  tabTemplateStyle: PropTypes.object,
  /**
   * Makes Tabs controllable and selects the tab whose value prop matches this prop.
   */
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
};

export default Tabs;
